import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import {BotatoChatManagerService} from '../service/botato-chat-manager.service';
import {BotatoWidgetManagerService, WindowState} from '../service/botato-widget-manager.service';
import {Subscription} from 'rxjs';
import {ChangeDetectionHelper} from '../ChangeDetectionHelper';

@Component({
    selector: 'botato-widget',
    templateUrl: './botato-widget.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BotatoWidgetComponent implements OnInit, OnDestroy, OnChanges {

    public static readonly SE_ST_KEY_WINDOWS_STATE: string = 'windowstate';

    @Input()
    public widgetTitle!: string;

    @Input()
    public showMaximize: boolean = true;

    @Input()
    public persistWindowState: boolean = true;

    @Output()
    public opened = new EventEmitter<boolean>();

    @Output()
    public closed = new EventEmitter<boolean>();

    public notificationMessage: string | undefined;
    public showNotification: boolean = false;
    public windowState!: WindowState;
    public maximized: boolean = false;

    private subscription = new Subscription();

    constructor(private botatoChatManager: BotatoChatManagerService,
                private botatoWidgetManager: BotatoWidgetManagerService,
                private applicationRef: ApplicationRef,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        if (this.persistWindowState) {
            const sessionWindowState = sessionStorage.getItem(BotatoWidgetComponent.SE_ST_KEY_WINDOWS_STATE);
            this.windowState = this.determineWindowState(sessionWindowState);
        } else {
            this.windowState = 'neveropened';
        }

        this.botatoWidgetManager.updateState(this.windowState);

        this.subscription.add(this.botatoWidgetManager.userNotification$.subscribe((message: string) => {
            if (this.windowState === 'neveropened' && !this.showNotification) {
                this.notificationMessage = message;
                this.showNotification = true;
                this.changeDetectorRef.markForCheck();
                this.applicationRef.tick();
            }
        }));

        this.subscription.add(this.botatoWidgetManager.chatOpened$.subscribe(() => {
            this.showNotification = false;
            this.setWindowState('open');
            this.storeWindowState();
            this.changeDetectorRef.markForCheck();
            setTimeout(() => this.botatoChatManager.focusInput());
        }));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (ChangeDetectionHelper.needsOnChangeATick(changes)) {
            this.applicationRef.tick();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public toggleWindow(): void {
        if (this.windowState === 'open') {
            this.closeWindow();
        } else {
            this.botatoWidgetManager.openChat();
        }
    }

    public closeWindow(): void {
        this.maximized = false;
        this.setWindowState('closed');
        this.storeWindowState();
    }

    public isWindowOpen(): boolean {
        return this.windowState === 'open';
    }

    public toggleMaximation(): void {
        this.maximized = !this.maximized;
        this.applicationRef.tick();
    }

    private determineWindowState(sessionWindowState: string | null): WindowState {
        // eslint-disable-next-line no-null/no-null
        if (sessionWindowState === null) {
            return 'neveropened';
        } else if (sessionWindowState === 'closed') {
            return 'closed';
        } else if (sessionWindowState === 'open') {
            return 'open';
        }
        throw new Error(`Unknown WindowState in SessionStorage: ${sessionWindowState}`);
    }

    private setWindowState(newWindowState: WindowState): void {
        if (newWindowState === 'open' && this.windowState !== 'open') {
            this.opened.emit();
        }
        if (newWindowState === 'closed' && this.windowState === 'open') {
            this.closed.emit();
        }
        this.windowState = newWindowState;
        this.botatoWidgetManager.updateState(this.windowState);
        this.applicationRef.tick();
    }

    private storeWindowState(): void {
        if (this.persistWindowState) {
            sessionStorage.setItem(BotatoWidgetComponent.SE_ST_KEY_WINDOWS_STATE, this.windowState);
        }
    }
}
